/**
 * Wiedererkennung ohne Benutzerkonto.
 *
 * Zwei Dinge liegen im Browser: eine Gerätekennung, die dauerhaft bleibt, und
 * je Ausfahrt ein Sitzungstoken. Die Kennung sorgt dafür, dass ein Neuladen,
 * ein Absturz oder ein leerer Akku niemanden aus seiner Ausfahrt wirft — er
 * kommt zurück und ist wieder derselbe Teilnehmer, mit derselben Farbe und
 * derselben Rolle.
 *
 * Die Kennung weist niemanden aus. Wer etwas darf, entscheidet allein das
 * Sitzungstoken.
 */

import { ENDPOINTS, type ParticipantRole } from '@konvoi/shared';

const DEVICE_KEY = 'konvoi.device';
const PROFILE_KEY = 'konvoi.profile';
const SESSION_PREFIX = 'konvoi.session.';

export interface StoredSession {
  tourId: string;
  participantId: string;
  sessionToken: string;
  webSocketUrl: string;
  joinCode: string;
  /**
   * Der Name der Ausfahrt — allein für die Anzeige auf der Startseite.
   *
   * `null` bei Sitzungen, die vor dieser Fassung entstanden sind, und solange
   * die Ausfahrt noch nicht geladen wurde. Er wird bei jedem Verbinden
   * nachgezogen; die Wahrheit steht im Server, hier steht eine Erinnerung.
   */
  tourName: string | null;
  /**
   * Die zuletzt bekannte eigene Rolle.
   *
   * Nicht die Wahrheit — die steht im Server und kommt mit `tour_state`. Aber
   * sie liegt beim Öffnen der App sofort vor, und ohne sie zeigt die
   * Fahrtansicht für einen Augenblick die Bedienung der falschen Rolle: Dem
   * Führungsfahrzeug blitzte die Navigationsfläche auf und verschwand wieder,
   * sobald der Server antwortete.
   *
   * `null` bei Sitzungen aus einer früheren Fassung. Dann bleibt zurückhaltend,
   * was von der Rolle abhängt, bis sie feststeht.
   */
  role: ParticipantRole | null;
}

/** Das zuletzt benutzte Profil — beim nächsten Mal mit einem Tipp übernehmbar. */
export interface StoredProfile {
  displayName: string;
  licensePlate: string;
  vehicleLabel: string | null;
  vehicleYear: number | null;
}

/**
 * Die Adresse der Echtzeitverbindung, aus der eigenen Herkunft abgeleitet.
 *
 * Das Backend nennt beim Beitritt zwar eine Adresse — die gilt aber einem
 * Client, der nicht von derselben Stelle ausgeliefert wird, etwa einer späteren
 * nativen Hülle. Die Weboberfläche kommt immer von demselben Ursprung wie die
 * Schnittstelle; sich daran zu halten erspart es, die öffentliche Adresse in
 * der Umgebung und die tatsächlich benutzte auseinanderlaufen zu lassen.
 */
export function webSocketUrlForOrigin(): string {
  const protokoll = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protokoll}//${window.location.host}${ENDPOINTS.webSocketPath}`;
}

export function deviceId(): string {
  const vorhanden = localStorage.getItem(DEVICE_KEY);
  if (vorhanden !== null && vorhanden.length >= 16) {
    return vorhanden;
  }

  const neu = `dev-${crypto.randomUUID()}`;
  localStorage.setItem(DEVICE_KEY, neu);
  return neu;
}

export function saveSession(session: StoredSession): void {
  localStorage.setItem(SESSION_PREFIX + session.tourId, JSON.stringify(session));
  localStorage.setItem('konvoi.lastTour', session.tourId);
}

export function loadSession(tourId: string): StoredSession | null {
  const roh = localStorage.getItem(SESSION_PREFIX + tourId);
  if (roh === null) {
    return null;
  }
  try {
    // Sitzungen aus einer früheren Fassung kennen den Namen noch nicht.
    const gespeichert = JSON.parse(roh) as StoredSession & {
      tourName?: string | null;
      role?: ParticipantRole | null;
    };
    return {
      ...gespeichert,
      tourName: gespeichert.tourName ?? null,
      role: gespeichert.role ?? null,
    };
  } catch {
    return null;
  }
}

/**
 * Hält Name der Ausfahrt und eigene Rolle in der Sitzung fest.
 *
 * Aufgerufen, sobald der Server beides nennt. Damit stehen sie beim nächsten
 * Start sofort zur Verfügung, ohne dass dafür eine Verbindung nötig wäre: der
 * Name auf der Schaltfläche der Startseite, die Rolle für die Bedienung der
 * Fahrtansicht. Beides wird überschrieben, sobald der Server sich meldet — hier
 * steht eine Erinnerung, nicht die Wahrheit.
 */
export function rememberTour(tourId: string, tourName: string, role: ParticipantRole): void {
  const sitzung = loadSession(tourId);
  if (sitzung === null || (sitzung.tourName === tourName && sitzung.role === role)) {
    return;
  }
  saveSession({ ...sitzung, tourName, role });
}

/** Die zuletzt gefahrene Ausfahrt — damit die App dort wieder aufmacht. */
export function lastTourId(): string | null {
  return localStorage.getItem('konvoi.lastTour');
}

export function forgetSession(tourId: string): void {
  localStorage.removeItem(SESSION_PREFIX + tourId);
  if (localStorage.getItem('konvoi.lastTour') === tourId) {
    localStorage.removeItem('konvoi.lastTour');
  }
}

export function saveProfile(profile: StoredProfile): void {
  localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
}

export function loadProfile(): StoredProfile | null {
  const roh = localStorage.getItem(PROFILE_KEY);
  if (roh === null) {
    return null;
  }
  try {
    return JSON.parse(roh) as StoredProfile;
  } catch {
    return null;
  }
}

export function forgetProfile(): void {
  localStorage.removeItem(PROFILE_KEY);
}
